/**
 * Spec 109-fix-help-quicklinks: maps Region API quick-link `name` values to
 * Help-panel i18n keys. Keys are prefixed with the `header:` namespace so
 * `QuickLinksPanel` (which uses the `bookingForm` namespace by default) can
 * resolve them via i18next's cross-namespace lookup.
 *
 * Names with no mapping fall back to `humanizeName(name)` at render time so
 * a freshly-introduced BE link is never dropped from the UI.
 */
import type { QuickLink } from '@store/models/booking';

export const REGION_QUICK_LINK_KEYS: Record<string, string> = {
  'MEETING-&-EVENT-GUIDELINES': 'header:helpPanel.quickLinks.meetingEventGuidelines',
  'EXECUTIVE-MEETING-CHECKLIST': 'header:helpPanel.quickLinks.executiveMeetingChecklist',
  'REFWEB-NORAM-SITES': 'header:helpPanel.quickLinks.refWebNoram',
  'EVENT-PROGRAM-REQUEST-FORM': 'header:helpPanel.quickLinks.eventProgramRequestForm',
};

// Icon names must match keys in QuickLinksPanel's ICON_MAP; unknown names fall
// back to QuickLinksPanel's FALLBACK_ICON (DocumentRegular) at render time.
export const REGION_QUICK_LINK_ICONS: Record<string, string> = {
  'MEETING-&-EVENT-GUIDELINES': 'ShieldRegular',
  'EXECUTIVE-MEETING-CHECKLIST': 'DocumentRegular',
  'REFWEB-NORAM-SITES': 'GlobeRegular',
  'EVENT-PROGRAM-REQUEST-FORM': 'CalendarLtrRegular',
};

// Static, in-app Quick Links appended after the Region API entries so they
// are always visible regardless of API availability. Icon names must exist in
// QuickLinksPanel's ICON_MAP.
export const STATIC_QUICK_LINKS: QuickLink[] = [
  {
    labelKey: 'header:helpPanel.quickLinks.buildingAccessRequirements',
    url: 'https://microsoft.sharepoint.com/sites/globalsecurity/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2Fglobalsecurity%2FShared%20Documents%2FGSAM%2FMicrosoft%20Building%20Access%20Requirements%2Epdf&parent=%2Fsites%2Fglobalsecurity%2FShared%20Documents%2FGSAM&share=cQqMU%2Ba4A2RHQZBNl2LD7%5FkNEgUCk6zMzEA7mV%2DFFdH%2DQ41l0A',
    icon: 'KeyRegular',
  },
  {
    labelKey: 'header:helpPanel.quickLinks.visitorsPolicy',
    url: 'https://microsoft.sharepoint.com/sites/globalsecurity/SitePages/Awareness-AccessControl-Visitors.aspx',
    icon: 'PersonRegular',
  },
];

/**
 * Produces a sentence-case readable label from a hyphen-delimited Region API
 * `name`. Used as a fallback when no entry exists in REGION_QUICK_LINK_KEYS.
 *
 * Examples:
 *   'MEETING-&-EVENT-GUIDELINES'   -> 'Meeting & Event Guidelines'
 *   'EXECUTIVE-MEETING-CHECKLIST'  -> 'Executive Meeting Checklist'
 *   'EVENT-PROGRAM-REQUEST-FORM'   -> 'Event Program Request Form'
 */
export function humanizeName(input: string): string {
  if (!input) return '';
  const normalized = input.replace(/-/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  if (normalized.length === 0) return '';
  return normalized
    .split(' ')
    .map((word) => {
      if (word.length === 0) return word;
      if (word.length === 1) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}
